import z from 'zod'
import {
  nullSafeBoolean,
  nullSafeNumber,
  nullSafeOptionalStringArray,
  nullSafeStringArray,
} from '../shared/schema'
import {
  SessionMuting,
  RoomStateVideoMethod,
  ChatbotProvider,
} from '../../types'

const sessionMutingSchema = z.nativeEnum(SessionMuting)
const videoMethodSchema = z.nativeEnum(RoomStateVideoMethod)
const chatbotProviderSchema = z.nativeEnum(ChatbotProvider)

const automaticRoomNameValuesSchema = z
  .object({
    adjectives: nullSafeStringArray([]),
    nouns: z.record(z.string(), z.string()),
  })
  .optional()

export const schema = z.object({
  adminRoleManagementEnabled: nullSafeBoolean(false),
  automaticRoomNameValues: z.preprocess((val) => {
    if (val) {
      const parsed = automaticRoomNameValuesSchema.safeParse(val)
      // if it's correctly formatted, use it
      // in any other case, undefined
      if (parsed.success) return parsed.data
    }

    return undefined
  }, automaticRoomNameValuesSchema),
  animationMethod: nullSafeBoolean(true),
  generateGoogleSlides: nullSafeBoolean(false),
  generateSessionSummary: nullSafeBoolean(true),
  rubricTestDesiredStdDeviation: nullSafeNumber(1.5),
  rubricTestMeanEpsilon: nullSafeNumber(0.9),
  sendInstructorEmails: nullSafeBoolean(false),
  sessionMuting: z.preprocess(
    (val) => val ?? SessionMuting.none,
    sessionMutingSchema
  ),
  sessionResultsGroupBalanceCoefficient: nullSafeNumber(0.5),
  sessionResultsShowAllConversationTooltips: nullSafeBoolean(true),
  showRubricArgumentEventMatchMinimum: nullSafeNumber(1), // float value
  useStrictPublishRequirements: nullSafeBoolean(false),
  videoMethod: z.preprocess(
    (val) => val ?? RoomStateVideoMethod.broadcast,
    videoMethodSchema
  ),
  showOrganizationsUI: nullSafeBoolean(false),
  audioSilenceDetectionEnabled: nullSafeBoolean(false),
  transcriptionLanguages: nullSafeOptionalStringArray(),
  // How long a "we can't hear you" problem must persist, with no success in
  // between, before the dialog opens. 0 disables the dialog. Shared by both
  // watchers that raise one - the transcription watcher and speech
  // reachability - because it is one dialog, with one repair, and an operator
  // who wants it wants it for either fault.
  transcriptProblemWaitDurationMS: nullSafeNumber(0), // in ms
  // When true, verifies that locally-detected speech (meeting_user_spoke_locally)
  // is confirmed by the server's active-speaker detection (meeting_user_spoke).
  // The master switch for that check - detection, analytics and its half of the
  // dialog above - and so the way to turn off the reachability trigger alone.
  spokeLocallyVerificationEnabled: nullSafeBoolean(true),
  // How long to wait after an un-muted local utterance before checking whether
  // the server confirmed it, absorbing delivery jitter between the local mic
  // event and the server active-speaker signal. In ms; negatives clamp to 0.
  // Deliberately generous: a delay that is too short reports healthy audio as
  // unreachable, writing a false meeting_user_spoke_not_confirmed row - and now
  // that a run of those can open a dialog, telling a user nobody can hear them
  // while the room hears them fine. Lower it once the confirmed/unconfirmed
  // split has proven stable.
  spokeLocallyVerificationDelayMS: nullSafeNumber(10000).transform((val) =>
    val < 0 ? 0 : val
  ),
  verifyInvitation: nullSafeBoolean(false),
  showAssignmentCSVResultsInMinutesAndSortingAsPercent: nullSafeBoolean(false),
  demoVideoMethod: z.preprocess((val) => {
    // this is protection against bad data - if it's streaming,
    // use it, otherwise default to broadcast
    if (val === RoomStateVideoMethod.streaming) return val
    return RoomStateVideoMethod.broadcast
  }, videoMethodSchema),
  // timeout in seconds, cannot be negative
  soloBotNudgeButtonTimeout: nullSafeNumber(0).transform((val) =>
    val < 0 ? 0 : val
  ),
  // Background-blur intensity passed to LiveKit's BackgroundProcessor.
  // 0 disables the feature entirely (no UI, no keyboard shortcut).
  backgroundBlurRadius: nullSafeNumber(0).transform((val) =>
    val < 0 ? 0 : val
  ),
  // Kill-switch for Safari only. When true, blur is force-disabled and the
  // UI is hidden for Safari users, regardless of `backgroundBlurRadius`.
  disableBackgroundBlurInSafari: nullSafeBoolean(false),
  hideSlideDeckPrice: nullSafeBoolean(false),
  useNextbookImportForm: nullSafeBoolean(false),
  enhancedSupportLinkParams: nullSafeBoolean(true),
  useSharedGoogleDocSlide: nullSafeBoolean(false),
  serviceWorkerKillSwitch: nullSafeBoolean(false),
  useNewGradingApi: nullSafeBoolean(false),
  lmsGradeSyncFromAdonisEnabled: nullSafeBoolean(false),
  delayedQuizGradeFromAdonis: nullSafeBoolean(false),
  useApiOnProfilePage: nullSafeBoolean(false),
  instructorsWhoCanViewTranscripts: nullSafeOptionalStringArray(),
  useV2SlideDeckCatalog: nullSafeBoolean(false),
  // Gates the customizable grading controls (per-rubric weights + score
  // mapping). When false, the grading dialog hides the scoring section and the
  // "customize weights" link and reverts to the legacy (narrower) width.
  customRubricsEnabled: nullSafeBoolean(false),
  // Pre-session AV check: when true the microphone check mark requires the
  // speech detector (VolumeAnalyzerWorklet) to fire on the preview track
  // instead of a single instant of raw analyser energy. A dead mic that
  // produces crackle has energy but no speech, so the raw check passes it
  // (see docs/incidents/support/2026-07-05-solo-room-no-transcription.md).
  avCheckSpeechDetectionEnabled: nullSafeBoolean(false),
  // In-meeting garbage-mic detection for solo rooms: if the user stays
  // unmuted for this long without a single transcript coming back, emit the
  // transcriptProblem flow ("we can't hear you"). 0 disables the check.
  unmutedNoTranscriptProblemDurationMS: nullSafeNumber(0), // in ms
  // When true, the A/V-room caption overlay consumes the WebVTT file for each
  // slide video instead of the legacy slide_caption Firestore subcollection.
  consumeVttCaptions: nullSafeBoolean(false),
  showExternalDemoToggle: nullSafeBoolean(false),
  // Gates the student's "Appeal final score" action on Session Results. When
  // false the score-breakdown dialog's footer renders Close only, as it does
  // today.
  studentGradeAppealsEnabled: nullSafeBoolean(false),
  // Which support chatbot the web app mounts: 'hubspot', 'fin', or nothing.
  //
  // Fails closed on purpose. A typo, an empty string, a stale value, or an
  // absent flag all resolve to `none` and mount no chatbot at all - a bot only
  // ever appears because someone chose it explicitly. Note this means the flag
  // must be set in Firestore before any chatbot shows up.
  chatbotProvider: z.preprocess((val) => {
    if (val === ChatbotProvider.hubspot || val === ChatbotProvider.fin)
      return val
    return ChatbotProvider.none
  }, chatbotProviderSchema),
  // Gates the admin "Content Library" sidebar item and its pages (Slide Decks,
  // Global Authors, Global Assets). Admin-only; super admins see it even when
  // this is off. See apps/web/src/hooks/contentLibraryAccess.ts.
  contentLibraryEnabled: nullSafeBoolean(false),
})

export const empty = Object.freeze(schema.parse({}))

export type FirestoreSettingsFeatureFlags = z.infer<typeof schema>
